/* eslint-disable react/prop-types */
import { useState, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { getPaymentLocations } from "api";
import DataTable from "components/organisms/DataTable/DataTable";
import useTableRequest from "hooks/useTableRequest";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import Button from "components/atoms/Button/Button";

// import components
import usePermissions from "hooks/usePermissions";
import AddEditPaymentLocationDialog from "./AddEditPaymentLocationDialog";
import "./PaymentLocation.scss";

// import hooks

const PaymentLocations = function () {
  const [showEditLocationDialog, setShowEditLocationDialog] = useState(false);
  const [selectedEditLocation, setSelectedEditLocation] = useState(null);

  const { getPaymentLocationLabel, permissions } = usePermissions();
  const label = getPaymentLocationLabel();
  const adminSettings = JSON.parse(permissions || "{}")?.permissions?.admin_settings;
  const viewOnly = Boolean(!adminSettings?.edit_locations);

  // Define a default UI for filtering, because all listing pages contain a global search

  const columns = useMemo(
    () => [
      {
        Header: <span className="pl-9 text-nowrap">{label}</span>,
        accessor: "location_name", // accessor is the "key" in the data
        Cell: ({ value }) => <p className="pl-9 pt-8 pb-8 mb-0">{value}</p>,
      },
      {
        Header: (
          <span className="pl-9">
            <FormattedMessage id="general.address" defaultMessage="ADDRESS" />
          </span>
        ),
        accessor: "address",
        Cell: ({ value }) => <p className="pt-8 pb-8 mb-0">{value}</p>,
      },
    ],
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const { instance, fetching, totalData, refetch } = useTableRequest(getPaymentLocations, columns, {
    params: {},
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      ...(globalFilter.payment_location?.length && { payment_location: globalFilter.payment_location }),
    }),
  });

  const closeEditLocationDialog = () => {
    setShowEditLocationDialog(false);
  };

  return (
    <>
      <AddEditPaymentLocationDialog
        showEditLocationDialog={showEditLocationDialog}
        closeEditLocationDialog={closeEditLocationDialog}
        selectedLocationReferenceNumber={selectedEditLocation}
        refetchPaymentLocations={refetch}
        viewOnly={viewOnly}
      />

      <DataTable
        tableInstance={instance}
        totalData={totalData}
        isLoading={fetching}
        draggable={false}
        onRowClick={(row) => {
          setSelectedEditLocation(row.original.reference_number);
          setShowEditLocationDialog(true);
        }}
        headerRight={
          <div className="d-flex justify-content-between mt-3 w-100">
            <div>
              <GlobalTableSearch
                currentFilters={instance.state.globalFilter}
                setFilters={instance.setGlobalFilter}
                filterKey="query"
                eventContext="User roles"
              />
            </div>
            {!viewOnly && (
              <div className="d-flex align-items-center ml-2">
                <Button
                  onClick={() => {
                    setSelectedEditLocation(null);
                    setShowEditLocationDialog(true);
                  }}
                >
                  <FormattedMessage
                    id="settings.payment-locations.addPaymentLocation"
                    defaultMessage="Add payment location"
                  />
                </Button>
              </div>
            )}
          </div>
        }
      />
    </>
  );
};

export default PaymentLocations;
